import React from "react"
import { CostBasisIds, Trade } from "../../.."
import {
  MovementLabelDto,
  NewInListMovementDto,
  NewInListTransactionDto,
  OtherPartyAliasDto,
} from "services/api/openapi"
import ContainerColumn, { ColumnsName } from "../../ContainerColumn"
import { SelectedRowsType } from "../../useBulkSelectorTransactionPage"
import { CustomCellDirection } from "../../useGenerateCell"
import GeneratedCell from "../../GeneratedCell"
import { DeveloppedRowsType } from "../../useDeveloppedTransactionPage"
import { ConfigTable } from "../../useConfigTable"
import { GetMovementDto } from "../../../../../services/api/routes/transactions"

export const getOtherParties = (loneMovements: NewInListMovementDto[], trades: Trade[]) => {
  const otherParties: OtherPartyAliasDto[] = []

  loneMovements.forEach((mvt) => {
    if (
      mvt.otherPartyAlias &&
      (mvt.otherPartyAlias.address || mvt.otherPartyAlias.alias || mvt.otherPartyAlias.blockchain)
    ) {
      otherParties.push(mvt.otherPartyAlias)
    }
  })
  trades.forEach((trade) => {
    if (
      trade.in.otherPartyAlias &&
      (trade.in.otherPartyAlias.address || trade.in.otherPartyAlias.alias || trade.in.otherPartyAlias.blockchain)
    ) {
      otherParties.push(trade.in.otherPartyAlias)
    }
    if (
      trade.out.otherPartyAlias &&
      (trade.out.otherPartyAlias.address || trade.out.otherPartyAlias.alias || trade.out.otherPartyAlias.blockchain)
    ) {
      otherParties.push(trade.out.otherPartyAlias)
    }
  })
  return otherParties
}

const getAllLabels = (loneMovements: NewInListMovementDto[], trades: Trade[]) => {
  const labels: Array<MovementLabelDto>[] = []
  loneMovements.forEach((mvt) => {
    labels.push(mvt.labels)
  })
  trades.forEach((trade) => {
    labels.push(trade.in.labels)
    labels.push(trade.out.labels)
  })
  return labels.flat(1)
}

const getDirection = (nbTrade: number, nbIn: number, nbOut: number, nbFee: number): CustomCellDirection => {
  if (nbTrade) return CustomCellDirection.TRADE
  if (nbIn) return CustomCellDirection.IN
  if (nbOut) return CustomCellDirection.OUT
  if (nbFee) return CustomCellDirection.FEE
  return CustomCellDirection.UNDEFINED
}

export const getMainMovement = (
  loneMovements: NewInListMovementDto[],
  trades: Trade[],
  nbIn: number,
  nbOut: number,
  nbFee: number,
) => {
  if (trades.length) return trades[0].out
  if (nbIn) return loneMovements.filter((mvt) => mvt.direction === "in")[0]
  if (nbOut) return loneMovements.filter((mvt) => mvt.direction === "out" && !mvt.isFee)[0]
  if (nbFee) return loneMovements.filter((mvt) => mvt.isFee)[0]
  return undefined
}

const getIncoming = (loneMovements: NewInListMovementDto[], trades: Trade[]) =>
  trades.length === 0 ? loneMovements.find((m) => m.direction === "in") : trades[0].in

const getOutgoing = (loneMovements: NewInListMovementDto[], trades: Trade[]) =>
  trades.length === 0 ? loneMovements.find((m) => m.direction === "out" && !m.isFee) : trades[0].out

export const generateSimpleTransaction = (
  transaction: NewInListTransactionDto,
  columnsNameArray: ColumnsName[],
  openWalletMovementDrawer: <PassedItemId extends string>(
    itemId: PassedItemId,
    formData?: (null extends PassedItemId ? undefined : undefined) | undefined,
  ) => void,
  openMovementLabelDrawer: <PassedItemId extends GetMovementDto>(
    itemId: PassedItemId,
    formData?: (null extends PassedItemId ? undefined : undefined) | undefined,
  ) => void,
  setCostBasisDrawerTransactionId: (ids: CostBasisIds | null) => void,
  loneMovements: NewInListMovementDto[],
  trades: Trade[],
  nbIn: number,
  nbOut: number,
  nbFee: number,
  selectedRows: SelectedRowsType | undefined,
  setSelectedRows: (row: SelectedRowsType | undefined) => void,
  developpedRows: DeveloppedRowsType | undefined,
  setDeveloppedRows: (row: DeveloppedRowsType | undefined) => void,
  columnsConfig: ConfigTable[],
  openTransactionLabelDrawer: <PassedItemId extends string>(
    itemId: PassedItemId,
    formData?: (null extends PassedItemId ? undefined : undefined) | undefined,
  ) => void,
) => {
  const otherPartiesAliases = getOtherParties(loneMovements, trades)
  const labels = getAllLabels(loneMovements, trades)
  const direction = getDirection(trades.length, nbIn, nbOut, nbFee)
  const mainMovement = getMainMovement(loneMovements, trades, nbIn, nbOut, nbFee)
  const fee = loneMovements.find((m) => m.isFee)
  const incoming = getIncoming(loneMovements, trades)
  const outgoing = getOutgoing(loneMovements, trades)

  return columnsNameArray.map((columnName: string) => (
    <ContainerColumn key={columnName} columnName={columnName} columnsConfig={columnsConfig}>
      <GeneratedCell
        columnName={columnName}
        dataToDisplay={{
          columnName,
          transactionDate: transaction.transactionDate,
          incoming,
          outgoing,
          fee,
          wallet: transaction.wallet,
          otherPartiesAliases,
          labels,
          id: transaction.id,
          direction,
          mainMovement,
          usdToFiatRate: transaction.usdToFiatRate,
          selectedRows,
          setSelectedRows,
          developpedRows,
          setDeveloppedRows,
          accountingJournal: mainMovement?.accountingJournal,
        }}
        openWalletDrawer={openWalletMovementDrawer}
        openMovementLabelDrawer={openMovementLabelDrawer}
        openTransactionLabelDrawer={openTransactionLabelDrawer}
        setCostBasisDrawerTransactionId={setCostBasisDrawerTransactionId}
      />
    </ContainerColumn>
  ))
}
